import React, { useState, useEffect } from 'react';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  Users,
  AlertTriangle,
  CheckCircle,
  Target,
  Activity
} from 'lucide-react';
import { Emergency, Responder, AnalyticsDashboard as AnalyticsData } from '../types';

interface AnalyticsDashboardProps {
  emergencies: Emergency[];
  responders: Responder[];
  className?: string;
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  emergencies,
  responders,
  className = ''
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d'>('24h');

  useEffect(() => {
    generateAnalytics();
  }, [emergencies, responders, timeRange]);

  const generateAnalytics = () => {
    const now = new Date();
    const timeRangeMs = {
      '24h': 24 * 60 * 60 * 1000,
      '7d': 7 * 24 * 60 * 60 * 1000,
      '30d': 30 * 24 * 60 * 60 * 1000
    };

    const cutoffTime = new Date(now.getTime() - timeRangeMs[timeRange]);
    const filteredEmergencies = emergencies.filter(e => e.timestamp >= cutoffTime);

    const totalEmergencies = filteredEmergencies.length;
    const resolvedEmergencies = filteredEmergencies.filter(e => e.status === 'resolved');
    const resolutionRate = totalEmergencies > 0 ? (resolvedEmergencies.length / totalEmergencies) * 100 : 0;

    const averageResponseTime = resolvedEmergencies.reduce((acc, e) => {
      return acc + (e.resolutionTime || 0);
    }, 0) / (resolvedEmergencies.length || 1);

    const availableResponders = responders.filter(r => r.isAvailable).length;
    const responderUtilization = ((responders.length - availableResponders) / responders.length) * 100;

    // Generate trends
    const emergencyTrends = generateEmergencyTrends(filteredEmergencies);
    const performanceMetrics = generatePerformanceMetrics(filteredEmergencies, responders);

    setAnalytics({
      totalEmergencies,
      averageResponseTime,
      resolutionRate,
      responderUtilization,
      emergencyTrends,
      performanceMetrics
    });
  };

  const generateEmergencyTrends = (emergencies: Emergency[]) => {
    const trends = [];
    const now = new Date();
    const days = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayEmergencies = emergencies.filter(e => {
        const eDate = new Date(e.timestamp);
        return eDate.toDateString() === date.toDateString();
      });

      trends.push({
        date,
        count: dayEmergencies.length,
        type: 'all' as any,
        severity: 'medium' as any
      });
    }

    return trends;
  };

  const generatePerformanceMetrics = (emergencies: Emergency[], responders: Responder[]) => {
    const metrics = [
      {
        metric: 'Response Time',
        value: analytics?.averageResponseTime || 0,
        trend: 'down' as const,
        target: 15
      },
      {
        metric: 'Resolution Rate',
        value: analytics?.resolutionRate || 0,
        trend: 'up' as const,
        target: 95
      },
      {
        metric: 'Responder Utilization',
        value: analytics?.responderUtilization || 0,
        trend: 'stable' as const,
        target: 75
      },
      {
        metric: 'Critical Emergencies',
        value: emergencies.filter(e => e.severity === 'critical').length,
        trend: 'down' as const,
        target: 0
      }
    ];

    return metrics;
  };

  const getEmergencyTypeStats = () => {
    const types = ['fire', 'medical', 'police', 'rescue', 'accident'];
    return types.map(type => {
      const count = emergencies.filter(e => e.type === type).length;
      const percentage = emergencies.length > 0 ? (count / emergencies.length) * 100 : 0;
      return { type, count, percentage };
    });
  };

  const getSeverityStats = () => {
    const severities = ['critical', 'high', 'medium', 'low'];
    return severities.map(severity => {
      const count = emergencies.filter(e => e.severity === severity).length;
      const percentage = emergencies.length > 0 ? (count / emergencies.length) * 100 : 0;
      return { severity, count, percentage };
    });
  };

  if (!analytics) {
    return (
      <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <Activity className="w-8 h-8 text-blue-600 animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-md ${className}`}>
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Emergency Analytics</h3>
              <p className="text-gray-600">Performance metrics and trends</p>
            </div>
          </div>
          
          <div className="flex rounded-lg overflow-hidden border border-gray-300">
            {(['24h', '7d', '30d'] as const).map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-8">
        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Emergencies</p>
                <p className="text-2xl font-bold text-blue-900">{analytics.totalEmergencies}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Resolution Rate</p>
                <p className="text-2xl font-bold text-green-900">{Math.round(analytics.resolutionRate)}%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600">Avg Response Time</p>
                <p className="text-2xl font-bold text-orange-900">{Math.round(analytics.averageResponseTime)}min</p>
              </div>
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Responder Utilization</p>
                <p className="text-2xl font-bold text-purple-900">{Math.round(analytics.responderUtilization)}%</p>
              </div>
              <Users className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Emergency Trends Chart */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Emergency Trends
          </h4>
          <div className="h-64 flex items-end justify-between gap-2">
            {analytics.emergencyTrends.map((trend, index) => {
              const maxCount = Math.max(...analytics.emergencyTrends.map(t => t.count));
              const height = maxCount > 0 ? (trend.count / maxCount) * 100 : 0;
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-blue-600 rounded-t transition-all duration-500 hover:bg-blue-700"
                    style={{ height: `${height}%`, minHeight: trend.count > 0 ? '8px' : '2px' }}
                    title={`${trend.count} emergencies on ${trend.date.toLocaleDateString()}`}
                  />
                  <div className="text-xs text-gray-600 mt-2 text-center">
                    {timeRange === '24h' ? 
                      trend.date.toLocaleTimeString([], { hour: '2-digit' }) :
                      trend.date.toLocaleDateString([], { month: 'short', day: 'numeric' })
                    }
                  </div>
                  <div className="text-xs font-medium text-gray-900">{trend.count}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Emergency Type Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Emergency Types</h4>
            <div className="space-y-3">
              {getEmergencyTypeStats().map(stat => (
                <div key={stat.type} className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 capitalize">{stat.type}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${stat.percentage}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{stat.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Severity Distribution</h4>
            <div className="space-y-3">
              {getSeverityStats().map(stat => {
                const colors = {
                  critical: 'bg-red-600',
                  high: 'bg-orange-600',
                  medium: 'bg-yellow-600',
                  low: 'bg-blue-600'
                };
                
                return (
                  <div key={stat.severity} className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 capitalize">{stat.severity}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`${colors[stat.severity as keyof typeof colors]} h-2 rounded-full transition-all duration-500`}
                          style={{ width: `${stat.percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-8">{stat.count}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-green-600" />
            Performance vs Targets
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {analytics.performanceMetrics.map((metric, index) => {
              const isOnTarget = metric.value >= metric.target;
              const percentage = (metric.value / metric.target) * 100;
              
              return (
                <div key={index} className="bg-white rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">{metric.metric}</span>
                    <div className={`text-xs px-2 py-1 rounded-full ${
                      metric.trend === 'up' ? 'bg-green-100 text-green-800' :
                      metric.trend === 'down' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {metric.trend}
                    </div>
                  </div>
                  <div className="text-xl font-bold text-gray-900 mb-1">
                    {metric.metric.includes('Rate') || metric.metric.includes('Utilization') ? 
                      `${Math.round(metric.value)}%` : 
                      Math.round(metric.value)
                    }
                  </div>
                  <div className="text-xs text-gray-600 mb-2">
                    Target: {metric.metric.includes('Rate') || metric.metric.includes('Utilization') ? 
                      `${metric.target}%` : 
                      metric.target
                    }
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ${
                        isOnTarget ? 'bg-green-600' : 'bg-red-600'
                      }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};